import { __decorate, __metadata, __param } from "tslib";
import intersection from 'lodash/intersection';
import _ from 'lodash';
import formatNumber from '@cores/utils/formatNumber';
import ptDialogAutoMounterManager from '@/services/PtDialogAutoMounterManager';
import { DetailServiceSymbol } from '@/services/DetailService';
import { inject, injectService, injectable } from '@/inversify.config';
import { i18nSymbol } from '@/i18n';
import { Emitter } from '@patsnap/tahiti-core';
const ExtractStructureDialog = () => import('@/presentation/components/folders/ExtractStructureDialog.vue');
const ExtractSequenceDialog = () => import('@/presentation/components/folders/ExtractSequenceDialog.vue');
const BulkTaggingDialog = () => import('@/presentation/pages/detail/basic/components/BulkTagging/BulkTaggingDialog.vue');
const SmartAnnotationConfirmDialog = () => import('@/presentation/components/SmartAnnotation/SmartAnnotationConfirmDialog.vue');
export const SELECTOR_TYPE = {
    INCLUDE: 'INCLUDE',
    EXCLUDE: 'EXCLUDE',
    RANGE: 'RANGE',
};
export const PageSelectorServiceSymbol = Symbol.for('PageSelectorService');
let PageSelectorService = class PageSelectorService {
    constructor(detailService) {
        this.detailService = detailService;
        this._onStateChangeEmitter = new Emitter();
        this.onStateChanged = this._onStateChangeEmitter.event;
        this.mode = SELECTOR_TYPE.INCLUDE;
        this._sourceIds = [];
        this.selectedIds = [];
        this.deselectedIds = [];
        this.rangeFrom = 0;
        this.rangeTo = 0;
        this.totalCount = 0;
    }
    get i18n() {
        return injectService(i18nSymbol);
    }
    get sourceIds() {
        return this._sourceIds;
    }
    set sourceIds(ids) {
        this._sourceIds = ids;
        this.emitStateChange();
    }
    get modeText() {
        let text = this.i18n.t(`selector.${this.detailService.detailType}.modeText`);
        switch (this.mode) {
            case SELECTOR_TYPE.INCLUDE: {
                const intersectionIds = intersection(this.selectedIds, this.sourceIds);
                if (intersectionIds.length > 0 && intersectionIds.length === this.sourceIds.length) {
                    text = this.i18n.t('selector.selectPage');
                }
                break;
            }
            case SELECTOR_TYPE.EXCLUDE:
                if (this.deselectedIds.length === 0) {
                    text = this.i18n.t('selector.selectAll');
                }
                break;
            case SELECTOR_TYPE.RANGE:
                text = this.i18n.t('selector.selectRange', { from: formatNumber(this.rangeFrom), to: formatNumber(this.rangeTo) });
                break;
        }
        return text;
    }
    get selectedCount() {
        let count = 0;
        switch (this.mode) {
            case SELECTOR_TYPE.INCLUDE:
                count = this.selectedIds.length;
                break;
            case SELECTOR_TYPE.EXCLUDE:
                count = this.totalCount - this.deselectedIds.length;
                break;
            case SELECTOR_TYPE.RANGE:
                count = this.rangeTo - this.rangeFrom + 1;
                break;
        }
        return count;
    }
    get isSelectAllMode() {
        return this.mode === SELECTOR_TYPE.EXCLUDE;
    }
    get isRangeMode() {
        return this.mode === SELECTOR_TYPE.RANGE;
    }
    get finalIds() {
        return this.isSelectAllMode ? this.deselectedIds : this.selectedIds;
    }
    /**
     * 触发状态变化事件
     * 当选择器状态发生变化时调用此方法
     */
    emitStateChange() {
        this._onStateChangeEmitter.fire();
    }
    /**
     *
     * @param {*} id
     * @param {*} nid 序号，用来判断range
     */
    isChecked(id, nid) {
        let checked = false;
        switch (this.mode) {
            case SELECTOR_TYPE.INCLUDE:
                checked = _.find(this.selectedIds, i => String(i) === String(id));
                break;
            case SELECTOR_TYPE.EXCLUDE:
                checked = !_.find(this.deselectedIds, i => String(i) === String(id));
                break;
            case SELECTOR_TYPE.RANGE:
                checked = nid >= this.rangeFrom && nid <= this.rangeTo;
                break;
        }
        return !!checked;
    }
    unselectAll() {
        this.mode = SELECTOR_TYPE.INCLUDE;
        this.selectedIds = [];
        this.deselectedIds = [];
        this.rangeFrom = undefined;
        this.rangeTo = undefined;
        this.emitStateChange();
    }
    selectAll() {
        this.mode = SELECTOR_TYPE.EXCLUDE;
        this.selectedIds = [];
        this.deselectedIds = [];
        this.rangeFrom = undefined;
        this.rangeTo = undefined;
        this.emitStateChange();
    }
    selectPage() {
        this.mode = SELECTOR_TYPE.INCLUDE;
        this.selectedIds = _.union(this.selectedIds, this.sourceIds);
        this.deselectedIds = [];
        this.rangeFrom = undefined;
        this.rangeTo = undefined;
        this.emitStateChange();
    }
    selectRange({ from = 0, to = 0 }) {
        this.rangeFrom = Number(from);
        this.rangeTo = Number(to);
        this.mode = SELECTOR_TYPE.RANGE;
        this.selectedIds = [];
        this.deselectedIds = [];
        this.emitStateChange();
    }
    selectOne(id) {
        switch (this.mode) {
            case SELECTOR_TYPE.INCLUDE:
                this.selectedIds = this.toggleId(this.selectedIds, id);
                break;
            case SELECTOR_TYPE.EXCLUDE:
                this.deselectedIds = this.toggleId(this.deselectedIds, id);
                break;
            case SELECTOR_TYPE.RANGE:
                this.mode = SELECTOR_TYPE.INCLUDE;
                this.selectedIds = [id];
                this.deselectedIds = [];
                this.rangeFrom = undefined;
                this.rangeTo = undefined;
                break;
        }
        this.emitStateChange();
    }
    toggleId(ids, id) {
        const index = ids.indexOf(id);
        if (index > -1) {
            ids.splice(index, 1);
        }
        else {
            ids.push(id);
        }
        return ids;
    }
    toggleExtractStructureDialog(bol) {
        if (bol) {
            ptDialogAutoMounterManager.openAsPromise(ExtractStructureDialog);
        }
        else {
            ptDialogAutoMounterManager.update(ExtractStructureDialog, {
                vIf: false,
            });
        }
    }
    toggleExtractSequenceDialog(bol) {
        if (bol) {
            ptDialogAutoMounterManager.openAsPromise(ExtractSequenceDialog);
        }
        else {
            ptDialogAutoMounterManager.update(ExtractSequenceDialog, {
                vIf: false,
            });
        }
    }
    async toggleBulkEditCustomFieldDialog(bol) {
        if (bol) {
            ptDialogAutoMounterManager.update(BulkTaggingDialog, {
                operationService: {
                    pageSelectorService: this,
                    detailService: this.detailService,
                },
            });
            await ptDialogAutoMounterManager.openAsPromise(BulkTaggingDialog);
        }
        else {
            ptDialogAutoMounterManager.toggleOpenState(BulkTaggingDialog, false);
        }
    }
    async toggleSmartAnnotationConfirmDialog(bol) {
        if (bol) {
            ptDialogAutoMounterManager.update(SmartAnnotationConfirmDialog, {
                operationService: {
                    pageSelectorService: this,
                },
            });
            ptDialogAutoMounterManager.openAsPromise(SmartAnnotationConfirmDialog);
        }
        else {
            ptDialogAutoMounterManager.update(SmartAnnotationConfirmDialog, {
                vIf: false,
            });
        }
    }
};
PageSelectorService = __decorate([
    injectable(),
    __param(0, inject(DetailServiceSymbol)),
    __metadata("design:paramtypes", [Object])
], PageSelectorService);
export { PageSelectorService };
